import { useEffect, useState } from "react";
import { api } from "../api/client";
import { useToast } from "../lib/toast";

type Status = "none" | "pending" | "accepted";
interface Parent {
  userId: string;
  displayName: string;
  email?: string;
  role?: "coach" | "parent";
  status: Status;
}

/**
 * Coach-only panel on the meet page. Two groups you can tap or drag onto the
 * "Invite" zone:
 *  - This team's parents  → sends a meet invite (email).
 *  - From your other teams → adds them to this team AND invites them, in one go.
 * Shows live status (Invite / Invited / Accepted).
 */
export function InviteParentsPanel({ meetId }: { meetId: string }) {
  const toast = useToast();
  const [items, setItems] = useState<Parent[]>([]);
  const [candidates, setCandidates] = useState<Parent[]>([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState<string>();
  const [dragOver, setDragOver] = useState(false);

  const load = () => {
    setLoading(true);
    api
      .getMeetInvitable(meetId)
      .then((r) => {
        setItems(r.items);
        setCandidates(r.candidates ?? []);
      })
      .catch(() => {
        setItems([]);
        setCandidates([]);
      })
      .finally(() => setLoading(false));
  };
  useEffect(load, [meetId]);

  // Invite an existing team member (email invite).
  const inviteMember = async (p: Parent) => {
    if (p.status !== "none") return;
    if (!p.email) return toast.error(`${p.displayName} has no email on file.`);
    setBusyId(p.userId);
    try {
      await api.inviteMeetMembers(meetId, [p.userId]);
      toast.success(`Invited ${p.displayName}`);
      load();
    } catch (e) {
      toast.error((e as Error).message);
    } finally {
      setBusyId(undefined);
    }
  };

  // Add a candidate (from another team) onto this team AND invite them.
  const inviteCandidate = async (p: Parent) => {
    setBusyId(p.userId);
    try {
      const res = await api.inviteCandidate(meetId, p.userId);
      toast.success(
        `Added ${p.displayName} to the team${res.invited ? " & invited" : ""}`,
      );
      load();
    } catch (e) {
      toast.error((e as Error).message);
    } finally {
      setBusyId(undefined);
    }
  };

  // Resolve a dropped/tapped id to the right action.
  const act = (id: string) => {
    const m = items.find((x) => x.userId === id);
    if (m) return inviteMember(m);
    const c = candidates.find((x) => x.userId === id);
    if (c) return inviteCandidate(c);
  };

  if (loading) return <p className="muted">Loading team…</p>;

  const label = (s: Status) =>
    s === "accepted" ? "Accepted" : s === "pending" ? "Invited" : "Invite";

  const chip = (p: Parent, kind: "member" | "candidate") => (
    <button
      key={p.userId}
      className={
        "invite-chip kind-" +
        kind +
        " status-" +
        p.status +
        (p.status === "none" ? "" : " done")
      }
      disabled={p.status !== "none" || busyId === p.userId}
      draggable={p.status === "none"}
      onDragStart={(e) => e.dataTransfer.setData("text/plain", p.userId)}
      onClick={() => (kind === "member" ? inviteMember(p) : inviteCandidate(p))}
      title={p.email ?? "No email on file"}
    >
      <span className="invite-chip-name">
        {p.displayName}
        {p.role === "coach" && (
          <span className="badge" style={{ marginLeft: "0.35rem" }}>
            Coach
          </span>
        )}
      </span>
      <span className="invite-chip-status">
        {p.status === "accepted"
          ? "✓ Accepted"
          : p.status === "pending"
            ? "✉ Invited"
            : kind === "candidate"
              ? "+ Add & invite"
              : "+ " + label(p.status)}
      </span>
    </button>
  );

  return (
    <div>
      <p className="muted" style={{ fontSize: "0.85rem", marginTop: 0 }}>
        Tap someone to invite — or drag them onto the zone below.
      </p>

      <div className="invite-cols">
        <div className="invite-col">
          <h4 className="invite-group-head">This team</h4>
          <div className="invite-parents">
            {items.length === 0 && (
              <span className="muted" style={{ fontSize: "0.85rem" }}>
                No members yet.
              </span>
            )}
            {items.map((p) => chip(p, "member"))}
          </div>
        </div>

        <div className="invite-col invite-col-candidates">
          <h4 className="invite-group-head">
            From your other teams
          </h4>
          <p className="muted" style={{ fontSize: "0.78rem", margin: "0 0 0.4rem" }}>
            Adds them to this team when invited.
          </p>
          <div className="invite-parents">
            {candidates.length === 0 && (
              <span className="muted" style={{ fontSize: "0.85rem" }}>
                No one else to pull in.
              </span>
            )}
            {candidates.map((p) => chip(p, "candidate"))}
          </div>
        </div>
      </div>

      {/* Drag-to-invite drop zone (handles both groups) */}
      <div
        className={"invite-dropzone" + (dragOver ? " over" : "")}
        onDragOver={(e) => {
          e.preventDefault();
          setDragOver(true);
        }}
        onDragLeave={() => setDragOver(false)}
        onDrop={(e) => {
          e.preventDefault();
          setDragOver(false);
          const id = e.dataTransfer.getData("text/plain");
          if (id) act(id);
        }}
      >
        ✉️ Drop a person here to invite
      </div>
    </div>
  );
}
